import { Injectable } from '@angular/core'
import { MediaFileBundle } from 'elements-sdk'

const PAGE_SIZE = 10

// eslint-disable-next-line @typescript-eslint/no-type-alias
export type NavigationFn<P> = (_: { offset?: number, limit?: number } & P) => Promise<(MediaFileBundle|undefined)[]> // a ML page might contain some number of folders at the beginning, these are replaced with undefined (mimicking the continuous layout of the file list)

@Injectable({ providedIn: 'root' })
export class MediaPrevNextNavigationService {
  active = false
  currentIndex: number
  total: number
  private cachedPage?: (MediaFileBundle|undefined)[]
  private cachedPageIndex?: number
  private fn?: NavigationFn<any>
  private params: any

  set<P> (args: { fn: NavigationFn<P>, params: P, currentIndex: number, total: number }): void {
    this.reset()
    this.fn = args.fn
    this.params = args.params
    this.currentIndex = args.currentIndex
    this.total = args.total
    this.active = true
  }

  async _setPage (page: number): Promise<void> {
    if (this.cachedPageIndex === page || !this.fn) {
      return
    }
    this.cachedPageIndex = page
    const params = {
      ...this.params,
      offset: (page - 1) * PAGE_SIZE,
      limit: PAGE_SIZE,
    }

    this.cachedPage = await this.fn(params)
  }

  async getItem (index: number): Promise<MediaFileBundle|undefined> {
    await this._setPage(Math.floor(index / PAGE_SIZE) + 1)
    return this.cachedPage?.[index % PAGE_SIZE]
  }

  setCurrentIndex (index: number): void {
    this.currentIndex = index
  }

  reset (): void {
    this.cachedPage = undefined
    this.cachedPageIndex = undefined
    this.active = false
  }

  async getPrevious (): Promise<any> {
    return this.getRelative(-1)
  }

  async getNext (): Promise<any> {
    return this.getRelative(1)
  }

  hasPrevious (): boolean {
    return this.currentIndex > 0
  }

  hasNext (): boolean {
    return this.currentIndex < this.total - 1
  }

  async getRelative (offset: number): Promise<MediaFileBundle|undefined> {
    return this.getItem((this.currentIndex + offset + this.total) % this.total)
  }
}
